import React, { useRef, FormEvent, useState, useEffect } from "react";
import styles from "../InputForm.module.css";
import Input from "../../../Components/Input/Input";
import TextArea from "../../../Components/TextArea/TextArea";
import { EncryptionService } from "../../../services/EncryptionService";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CheckboxList, {
  CheckboxOption,
} from "../../../Components/CheckList/Checklist";
import useAPI from "../../../Hooks/useAPI";
import debounce from "lodash.debounce";
import { useUser } from "../../../Context/UserContext";
import Button2 from "../../../Components/Button2/Button2";
import "./RequestInfoForm.css";
import RadioList from "../../../Components/CheckList/RadioList";

interface ContactFormData {
  companyName: string;
  title: string;
  firstname: string;
  lastname: string;
  email: string;
  stakeholder: string;
  hearaboutus: string;
  requInfo: string; //[],
  message: string;
  ipaddress: string;
  datasource: string;
}

const InputForm: React.FC = (): JSX.Element => {
  const { user, setUser } = useUser();
  const [encryptionService] = useState(() => new EncryptionService());
  const [checkListSelected, setCheckListSelected] = useState<string[]>([]);
  const [stakeholderSelected, setStakeholderSelected] = useState<string>();
  const [hearAboutSelected, setHearAboutSelected] = useState<string>();
  const {
    postSendReqInfoEmail,
    saveContact,
    insertContactRequestInfo,
    submitLead,
  } = useAPI();
  const [cidn, setCIDN] = useState<number | null>(null);
  const [, setEmail] = useState("");
  const [, setFirstName] = useState("");
  const [, setLastName] = useState("");
  const [, setCompanyName] = useState("");
  const [, setTitle] = useState("");

  // --- Initialize existing contact ID from localStorage ---
  useEffect(() => {
    const storedId = Number(localStorage.getItem("partial_contact_id"));
    if (storedId) {
      setCIDN(storedId);
    }
  }, []);

  const fetchIp = async () => {
    try {
      const res = await fetch("https://api.ipify.org/?format=json");
      const data = await res.json();

      setFormData((prev) => ({
        ...prev,
        ipaddress: data.ip,
      }));

      console.log("Fetched IP:", data.ip);
    } catch (err) {
      console.error("IP fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchIp();
  }, []);

  const [formData, setFormData] = useState<ContactFormData>({
    companyName: "",
    title: "",
    firstname: "",
    lastname: "",
    email: "",
    stakeholder: "",
    hearaboutus: "",
    requInfo: "", //[]
    message: "",
    ipaddress: "",
    datasource: "requestinfo",
  });

  // const options: CheckboxOption[] = [
  //   { label: "Business Information Meeting", value: "BuisnessRM" },
  //   { label: "Software/Data Information Meeting", value: "SoftwareRM" },
  //   { label: "Engine Tech Information Meeting", value: "TechRM" },
  //   { label: "Protected Products Information", value: "ProductsPI" },
  //   { label: "Protected Investor Information", value: "InvestorPI" },
  //   { label: "Protected News Information", value: "NewsPI" },
  // ];
  const options: CheckboxOption[] = [
    { label: "Business Information Meeting", value: "BuisnessRM" },
    { label: "Software/Data Information Meeting", value: "SoftwareRM" },
    { label: "Engine Tech Information Meeting", value: "TechRM" },
    { label: "Protected Products Information", value: "ProductsPI" },
    { label: "Protected Investor Information", value: "InvestorPI" },
    { label: "Protected News Information", value: "NewsPI" },
  ];

  // const options: CheckboxOption[] = [
  //   { label: "Business Information Meeting", value: "BuisnessRM" },
  //   { label: "Software/Data Information Meeting", value: "SoftwareRM" },
  //   { label: "Engine Tech Information Meeting", value: "TechRM" },
  //   { label: "Protected Products Information", value: "ProductsPI" },
  //   { label: "Protected Investor Information", value: "InvestorPI" },
  //   { label: "Protected News Information", value: "NewsPI" },
  // ];

  const hearAboutUsOptions: CheckboxOption[] = [
    { label: "Web", value: "Web" },
    { label: "LinkedIn", value: "LinkedIn" },
    { label: "Public Event", value: "Public Event" },
    { label: "Referral", value: "Referral" },
  ];

  const StakeHolderOptions: CheckboxOption[] = [
    { label: "OEM", value: "43" },
    {
      label: "Engines or Parts Manufacture",
      value: "44",
    },
    { label: "Engineer", value: "45" },
    { label: "Potential Investor", value: "46" },
    { label: "Strategic Partner", value: "47" },
    { label: "Press Media", value: "48" },
    { label: "Education", value: "49" },
    { label: "Career", value: "50" },
    { label: "Other", value: "51" },
  ];

  useEffect(() => {
    // 1. Always initialize with whatever user context is currently available
    if (user) {
      setFormData({
        companyName: user.companyName || "",
        title: user.title || "",
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        stakeholder: "",
        hearaboutus: "",
        requInfo: "", //[]
        message: "",
        ipaddress: "",
        datasource: "requestinfo",
      });
    }
  }, [user?.id]); // runs when user is ready

  useEffect(() => {
    encryptionService.loadPublicKey().catch((err) => {
      console.error("Failed to load public key:", err);
    });
  }, [encryptionService]);

  const form = useRef<HTMLFormElement | null>(null);

  const navigate = useNavigate();

  const location = useLocation();

  // Allow the form to be pre-filled via query params, e.g.
  // ?email=jane@example.com&firstname=Jane&lastname=Doe&company=Acme&title=CTO
  // &stakeholder=45&hearaboutus=Referral&requInfo=BuisnessRM,TechRM&message=Hi
  // Reapplies whenever the user context resets the form so query params always win.
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const email = params.get("email") ?? "";
    const firstname = params.get("firstname") ?? "";
    const lastname = params.get("lastname") ?? "";
    const companyName = params.get("company") ?? params.get("companyName") ?? "";
    const title = params.get("title") ?? "";
    const message = params.get("message") ?? "";
    const stakeholder = params.get("stakeholder") ?? "";
    const hearaboutus = params.get("hearaboutus") ?? "";
    const requInfo = (params.get("requInfo") ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter((value) => options.some((option) => option.value === value));

    const hasAny =
      email ||
      firstname ||
      lastname ||
      companyName ||
      title ||
      message ||
      stakeholder ||
      hearaboutus ||
      requInfo.length;

    if (!hasAny) return;

    setFormData((prev) => ({
      ...prev,
      email: email || prev.email,
      firstname: firstname || prev.firstname,
      lastname: lastname || prev.lastname,
      companyName: companyName || prev.companyName,
      title: title || prev.title,
    }));

    if (message) {
      const messageEl = document.getElementById(
        "message",
      ) as HTMLTextAreaElement | null;
      if (messageEl) messageEl.value = message;
    }

    if (stakeholder) setStakeholderSelected(stakeholder);
    if (hearaboutus) setHearAboutSelected(hearaboutus);
    if (requInfo.length) setCheckListSelected(requInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.id]);

  const sendEmail = async (e: FormEvent) => {
    e.preventDefault();

    const submitter = (e as unknown as SubmitEvent)
      .submitter as HTMLButtonElement;
    const buttonClicked = submitter?.id || submitter?.name;

    if (!form.current) return;

    const formData = new FormData(e.currentTarget as HTMLFormElement);

    const updatedFormData = {
      companyName: formData.get("companyName"),
      title: formData.get("title"),
      firstname: formData.get("firstname"),
      lastname: formData.get("lastname"),
      email: formData.get("email"),
      stakeholder: stakeholderSelected,
      hearaboutus: hearAboutSelected,
      requInfo: checkListSelected,
      message: formData.get("text"),
    };

    const requestInfoFormData = {
      cidn: cidn,
      request: checkListSelected,
      message: formData.get("text"),
      how_did_you_hear_about_us: hearAboutSelected,
      stakeholder: stakeholderSelected,
    };

    //Insert Request Info for Contact
    await insertContactRequestInfo(requestInfoFormData);

    const leadFormData = {
      title: `WebForm-${updatedFormData.firstname}${updatedFormData.lastname}`,
      firstName: `${updatedFormData.firstname}`,
      lastName: ` ${updatedFormData.lastname}`,
      email: updatedFormData.email,
      person_title: updatedFormData.title,
      company: updatedFormData.companyName,
      stakeholder: Number(updatedFormData.stakeholder),
      hear_about: updatedFormData.hearaboutus,
      req_type: updatedFormData.requInfo,
      message: updatedFormData.message,
    };

    //Create Lead in Pipedrive
    await submitLead(leadFormData);

    // Send email
    console.log(updatedFormData);

    postSendReqInfoEmail(
      updatedFormData.email as string,
      updatedFormData.firstname as string,
      updatedFormData.lastname as string,
      updatedFormData.title as string,
      updatedFormData.stakeholder as string,
      updatedFormData.hearaboutus as string,
      updatedFormData.requInfo as string[],
      updatedFormData.message as string,
      updatedFormData.companyName as string,
    );

    await new Promise((resolve) => setTimeout(resolve, 100));
    form.current.reset();
    navigate("../sent");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
  };

  // --- Save data after tabbing off each field ---
  const debouncedSaveRef = React.useRef(
    debounce(
      async (
        updatedData: ContactFormData,
        cidn: Number | null,
        fieldname: string | null,
      ) => {
        try {
          const bReturnAll = fieldname === "email"; //set breturnall to true if fieldname is email
          //console.log("payload: ", updatedData);
          const response = await saveContact(updatedData, bReturnAll, cidn);
          const out_cidn = response.out_cidn;

          //Only update all the fields in the page if we have a match by email address
          if (bReturnAll) {
            const returnedEmail = response?.email;
            const returnedFirstName = response?.firstname;
            const returnedLastName = response?.lastname;
            const returnedCompanyName = response?.companyname;
            const returnedTitle = response?.title;

            setEmail(returnedEmail);
            setFirstName(returnedFirstName);
            setLastName(returnedLastName);
            setCompanyName(returnedCompanyName);
            setTitle(returnedTitle);

            // Update the form field so the input shows it
            setFormData((prev) => ({
              ...prev,
              email: returnedEmail || "",
              firstname: returnedFirstName || "",
              lastname: returnedLastName || "",
              companyName: returnedCompanyName || "",
              title: returnedTitle || "",
            }));
          }

          // If new partial ID returned, store it
          const returnedId = out_cidn;
          if (returnedId && !cidn) {
            localStorage.setItem("partial_contact_id", returnedId);
            setCIDN(returnedId);
          }
        } catch (error) {
          console.error("Error auto-saving contact:", error);
        }
      },
      600,
    ), // control debounce timing here
  ).current;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Update form state
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Send to debounced auto-save
      debouncedSaveRef(updated, cidn, name);

      return updated;
    });
  };

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("partial_contact_id");
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      debouncedSaveRef.flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      debouncedSaveRef.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      // Component is unmounting → user left the page
      localStorage.removeItem("partial_contact_id");
    };
  }, []);

  return (
    <div className="req-info">
      <form id="form" ref={form} onSubmit={sendEmail}>
        <div className="form-group">
          <Input
            label="Email"
            type="email"
            id="email"
            placeholder="Email"
            required={true}
            name="email"
            value={formData?.email ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="First Name"
            type="text"
            id="firstname"
            placeholder="First Name"
            required={true}
            name="firstname"
            value={formData?.firstname ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="Last Name"
            type="text"
            id="lastname"
            placeholder="Last Name"
            required={true}
            name="lastname"
            value={formData?.lastname ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="Company Name"
            type="text"
            id="companyName"
            placeholder="Company Name"
            required={true}
            name="companyName"
            value={formData?.companyName ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="Title"
            type="text"
            id="companyTitle"
            placeholder="Title"
            required={false}
            name="title"
            value={formData?.title ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <div className="request-info-form-checkboxlist">
            <RadioList
              options={StakeHolderOptions}
              defaultDropdownLabel="Please Select One"
              selectedValue={stakeholderSelected}
              required={true}
              onChange={setStakeholderSelected}
              label="Interest"
            />
          </div>
          <div className="request-info-form-checkboxlist">
            <RadioList
              options={hearAboutUsOptions}
              defaultDropdownLabel="Please Select One"
              selectedValue={hearAboutSelected}
              required={true}
              onChange={setHearAboutSelected}
              label="How did you hear about us?"
            />
          </div>
          <div className="request-info-form-checkboxlist">
            <CheckboxList
              options={options}
              defaultDropdownLabel="Please Select One"
              selectedValues={checkListSelected}
              onChange={setCheckListSelected}
              label="Request"
              required={false}
            />
          </div>
        </div>
        <TextArea
          label="Message"
          id="message"
          placeholder="Message"
          required={false}
          rows={8}
          cols={20}
          name="text"
        />
        <div className="needPermission">
          <h4 className="disclaimer">
            We will process your request over the next few days. Some of the
            requests are based on user account. Other requests require a MNDA.
            If you have not already done so please{" "}
            <Link to="../user-login">register</Link>.
          </h4>
        </div>
        <Button2
          name="send"
          text="Send"
          type="submit"
          classname="request-info-cta-btn"
        />
      </form>
      <div className={styles.linkContainer}></div>
    </div>
  );
};

export default InputForm;
